import * as React from 'react';
import { useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { FeeOptionEntity } from 'Models/Entities';
import { LinkedBookingPricingResult } from 'Services/Api/_HumanWritten/PricingService/PricingResult';
import { tripSummaryLocationType } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/TripBookingSummaryCard';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { NumberInput } from 'Views/Components/New/NumberInput';
import { SelectInput } from 'Views/Components/New/SelectInput';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { bookingType, feeApplicationLevel, wizardModes } from 'Models/Enums';
import { GetFilteredFees } from 'Util/_HumanWritten/FeeOptionsUtils';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import {
	addFeeToTransaction,
	restoreCheckInForfeitFee,
} from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';

export interface FeeDto {
	id: string;
	name: string;
	description: string;
	amount: number;
	isPercentage: boolean;
	applicationLevel: feeApplicationLevel;
	vehicleBooking: boolean;
	passengerBooking: boolean;
	freightBooking: boolean;
}

/**
 * Not a fee option — the sentinel for "put the check-in forfeit fee back". It is offered in the same
 * picker because that is where staff go to put a charge back on a transaction, but it is not a
 * FeeOption row: there is nothing to configure and the amount is not ours to choose. The server
 * re-measures the reduction and restores exactly what removing it took away.
 */
export const RESTORE_CHECK_IN_FORFEIT_FEE_ID = '__restore-check-in-forfeit-fee__';

async function fetchFeeOptions(): Promise<FeeOptionEntity[]> {
	const res = await axios.get<FeeOptionEntity[]>('/api/entity/FeeOptionEntity/options');
	return (res.data ?? []).map(row => new FeeOptionEntity(row));
}

interface AddFeeModalContentProps {
	linkedBookings: LinkedBookingPricingResult[];
	tripSummaryLocation: tripSummaryLocationType;
	wizardMode: wizardModes;
	transactionFeeIds: string[];
	bookingTypes?: bookingType[];
	/** Whether to offer putting a removed check-in forfeit fee back. */
	canRestoreCheckInForfeitFee?: boolean;
	/** The tenant's wording for that fee, so the picker matches the cart. */
	checkInForfeitFeeLabel?: string;
	/**
	 * Reports the currently selected fee id back to the caller so it can apply it on confirm, and whether
	 * that fee works out to $0 here — in which case confirming would add nothing.
	 */
	onSelectedFeeChange: (feeId: string, addsNothing: boolean) => void;
}

function AddFeeModalContent({
	linkedBookings,
	tripSummaryLocation,
	wizardMode,
	transactionFeeIds,
	bookingTypes,
	canRestoreCheckInForfeitFee,
	checkInForfeitFeeLabel,
	onSelectedFeeChange,
}: AddFeeModalContentProps) {
	const { data: fees } = useQuery({
		queryKey: ['fee-options'],
		queryFn: fetchFeeOptions,
	});

	const feeOptions = useMemo<FeeDto[]>(
		() =>
			(GetFilteredFees(fees ?? [], tripSummaryLocation, wizardMode, transactionFeeIds, bookingTypes) ?? []).map(
				x => ({
					id: x.id,
					name: x.name,
					description: x.description,
					amount: x.feeAmount,
					isPercentage: !x.fixedAmountFee,
					applicationLevel: x.applicationLevel,
					vehicleBooking: x.vehicleBooking,
					passengerBooking: x.passengerBooking,
					freightBooking: x.freightBooking,
				}),
			),
		[fees, tripSummaryLocation, wizardMode, transactionFeeIds, bookingTypes],
	);

	const [selectedFeeId, setSelectedFeeId] = useState('');
	const [selectedFeeAmount, setSelectedFeeAmount] = useState<number | undefined>(undefined);

	const onSelectFee = (feeId: string) => {
		setSelectedFeeId(feeId);

		const feeToApply = feeOptions.find(x => x.id === feeId);
		if (!isNotNullOrUndefined(feeToApply)) {
			setSelectedFeeAmount(undefined);
			onSelectedFeeChange(feeId, false);
			return;
		}

		const amount = calculateFeeAmount(feeToApply, linkedBookings);
		setSelectedFeeAmount(amount);
		onSelectedFeeChange(feeId, amount === 0);
	};

	const feeTypeOptions = [
		...(canRestoreCheckInForfeitFee
			? [
					{
						label: checkInForfeitFeeLabel || 'Check-in forfeit fee',
						description:
							'Puts back the value this check-in reduction gave up. The amount is re-measured '
							+ 'from the reduction, so it is exactly what removing it took away.',
						value: RESTORE_CHECK_IN_FORFEIT_FEE_ID,
					},
				]
			: []),
		...feeOptions.map(x => ({ label: x.name, description: x.description, value: x.id })),
	];
	const selectedFeeOption = feeTypeOptions.find(x => x.value === selectedFeeId) ?? null;
	const selectedFee = feeOptions.find(x => x.id === selectedFeeId) ?? null;

	// Restoring the forfeit fee has no amount to show: the server re-measures the reduction when it puts
	// the fee back, precisely so the pair stays cash-neutral, and nothing on this transaction still
	// carries the figure that was removed. The read-only amount field therefore sat on its "..."
	// placeholder, which reads as a value that failed to load. Say what will happen instead.
	const isRestoringForfeitFee = selectedFeeId === RESTORE_CHECK_IN_FORFEIT_FEE_ID;

	// A fee that works out to $0 — in practice a percentage of a free change — used to be accepted and then
	// add nothing, which looked like the button had failed. Say so here instead; confirming is refused while
	// it is selected (see RenderAddServiceFeeModal).
	const addsNothing = isNotNullOrUndefined(selectedFee) && selectedFeeAmount === 0;

	return (
		<div className="add-service-fee-modal-content flex flex-col gap-4">
			<p className="modal-description">Select a fee to add to the booking.</p>
			{/* data-test-id wrapper — SelectInput doesn't forward arbitrary props to the DOM */}
			<div data-test-id="add-service-fee-fee-type">
				<SelectInput
					label="Fee type"
					placeholder="Select fee"
					required
					options={feeTypeOptions}
					value={selectedFeeOption}
					renderOption={option => (
						<div>
							<div>{option.label}</div>
							{isNotNullOrUndefined(option.description) && option.description !== '' && (
								<div className="text-xs text-gray-500">{option.description}</div>
							)}
						</div>
					)}
					onChange={option => {
						const value = (option as { value?: string } | null)?.value;
						if (typeof value === 'string') {
							onSelectFee(value);
						}
					}}
				/>
			</div>
			{isNotNullOrUndefined(selectedFee) && (
				<div className="flex flex-col gap-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
					{selectedFee.description.trim() !== '' && (
						<FeeDetail label="Description">{selectedFee.description}</FeeDetail>
					)}
					<FeeDetail label="Applies to">{formatBookingTypeList(getAppliesTo(selectedFee))}</FeeDetail>
					<FeeDetail label="Amount">{formatFeeAmount(selectedFee)}</FeeDetail>
				</div>
			)}
			{isRestoringForfeitFee && (
				<div
					className="flex flex-col gap-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm"
					data-test-id="add-service-fee-restore-forfeit-detail"
				>
					<FeeDetail label="Applies to">This booking&apos;s check-in change</FeeDetail>
					<FeeDetail label="Amount">
						Matched to the reduction — worked out when you add it, so the customer is charged
						exactly what removing the fee took off
					</FeeDetail>
				</div>
			)}
			{!isRestoringForfeitFee && (
				<NumberInput
					id="add-fee-modal-amount"
					label="Fee amount"
					startAdornment="$"
					value={selectedFeeAmount}
					placeholder="..."
					readOnly
				/>
			)}
			{addsNothing && (
				<div data-test-id="add-service-fee-adds-nothing">
					<MessageBox status="warning" title="Nothing to add" slim>
						{selectedFee.isPercentage
							? `This fee is ${selectedFee.amount}% of the booking's price, and this booking costs `
								+ '$0.00, so it would add nothing. Choose a fixed-amount fee to charge for it.'
							: 'This fee works out to $0.00 on this booking, so it would add nothing.'}
					</MessageBox>
				</div>
			)}
		</div>
	);
}

export const RenderAddServiceFeeModal = async (
	linkedBookings: LinkedBookingPricingResult[],
	tripSummaryLocation: tripSummaryLocationType,
	wizardMode: wizardModes,
	transactionFeeIds: string[],
	forCheckIn: boolean,
	transactionId?: string,
	bookingTypes?: bookingType[],
	canRestoreCheckInForfeitFee?: boolean,
	checkInForfeitFeeLabel?: string,
) => {
	// The modal content owns the fetch + selection state; we just need the chosen fee id back
	// when the user confirms.
	const selection = { feeId: '', addsNothing: false };

	const confirmed = await newConfirmModal(
		'Add service fee',
		<AddFeeModalContent
			linkedBookings={linkedBookings}
			tripSummaryLocation={tripSummaryLocation}
			wizardMode={wizardMode}
			transactionFeeIds={transactionFeeIds}
			bookingTypes={bookingTypes}
			canRestoreCheckInForfeitFee={canRestoreCheckInForfeitFee}
			checkInForfeitFeeLabel={checkInForfeitFeeLabel}
			onSelectedFeeChange={(feeId, addsNothing) => {
				selection.feeId = feeId;
				selection.addsNothing = addsNothing;
			}}
		/>,
		{
			cancelText: 'Cancel',
			confirmText: 'Confirm',
			actionClassName: 'add-service-fee-modal',
			resolveOnCancel: false,
			// Keep the modal open on a fee that would add nothing; the content is already explaining why.
			onBeforeConfirm: () => !selection.addsNothing,
		},
	);

	if (!confirmed) {
		return;
	}

	if (!isNotNullOrUndefined(transactionId)) {
		return;
	}

	if (selection.feeId === RESTORE_CHECK_IN_FORFEIT_FEE_ID) {
		await restoreCheckInForfeitFee(transactionId);
		return;
	}

	await addFeeToTransaction(transactionId, selection.feeId, forCheckIn);
};

/** A labelled row in the selected-fee details box. */
function FeeDetail({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="flex flex-col gap-0.5">
			<span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
			<span className="text-gray-800">{children}</span>
		</div>
	);
}

/**
 * Human-readable fee amount. Percentages read as a share of their base ("10% of transaction");
 * fixed amounts show the dollar value, qualified with "per booking" only when charged per booking
 * (a transaction-level fixed fee is just the flat amount, since a cart is a single transaction).
 */
function formatFeeAmount(fee: FeeDto): string {
	const level = fee.applicationLevel === 'Booking' ? 'booking' : 'transaction';
	if (fee.isPercentage) {
		return `${fee.amount}% of ${level}`;
	}
	const amount = `$${fee.amount.toFixed(2)}`;
	return fee.applicationLevel === 'Booking' ? `${amount} per booking` : amount;
}

/** The booking types a fee applies to, as display labels. */
function getAppliesTo(fee: FeeDto): string[] {
	const appliesTo: string[] = [];
	if (fee.vehicleBooking) appliesTo.push('Vehicle');
	if (fee.passengerBooking) appliesTo.push('Passenger');
	if (fee.freightBooking) appliesTo.push('Freight');
	return appliesTo;
}

/** Joins booking-type labels into a readable list, e.g. ['Vehicle', 'Passenger', 'Freight'] → "Vehicle, Passenger and Freight". */
function formatBookingTypeList(items: string[]): string {
	if (items.length <= 1) {
		return items[0] ?? '';
	}
	return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

const roundToCents = (value: number): number => Math.round(value * 100) / 100;

function calculateFeeAmount(feeOption: FeeDto, linkedBookings: LinkedBookingPricingResult[]): number {
	// Collect the price of every applicable leg (departure + return count as separate legs/bookings,
	// matching the server: each is its own BookingEntity). Mirrors AddFeeToTransactionUseCase.
	const legPrices: number[] = [];

	const collect = (type: bookingType, shouldApply: boolean) => {
		if (!shouldApply) return;

		const relevant = linkedBookings.filter(x => x.departureBooking.bookingType === type);

		for (const booking of relevant) {
			legPrices.push(booking.departureBooking.totalPrice ?? 0);
			if (isNotNullOrUndefined(booking.returnBooking)) {
				legPrices.push(booking.returnBooking.totalPrice ?? 0);
			}
		}
	};

	collect('PASSENGER_ONLY', feeOption.passengerBooking);
	collect('VEHICLE', feeOption.vehicleBooking);
	collect('FREIGHT', feeOption.freightBooking);

	if (legPrices.length === 0) return 0;

	if (feeOption.applicationLevel === 'Booking') {
		// Per booking (leg): fixed is charged once per leg; a percentage is taken of each leg's
		// price (rounded per leg, matching the server) and summed.
		return feeOption.isPercentage
			? legPrices.reduce((sum, price) => sum + roundToCents((Math.abs(price) * feeOption.amount) / 100), 0)
			: feeOption.amount * legPrices.length;
	}

	// Per transaction: fixed is the flat amount once; a percentage is taken of the combined total.
	const total = legPrices.reduce((sum, price) => sum + price, 0);
	return feeOption.isPercentage ? roundToCents((Math.abs(total) * feeOption.amount) / 100) : feeOption.amount;
}
